import {
  Avatar,
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material"
import {
  useMutation,
  useQueries,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query"
import moment from "moment"
import {Fragment, useEffect, useState, type FC} from "react"
import {decodeToken} from "react-jwt"
import {useNavigate} from "react-router-dom"
import {ReactSVG} from "react-svg"
import {SupportModal, TextWithTooltip, Tooltip} from "../.."
import {useConfigProvider} from "../../../config"
import {
  getDeliverableExtension,
  iconMap,
  stringToAvatar,
  clickableTypes,
  contentTypeMap,
} from "../../../helpers"
import useStorage, {StorageData} from "../../../hooks/useStorage"
import {
  deliverableEndpoints,
  fabricEndpoints,
  projectsEndpoints,
} from "../../../services/"
import {useClientSessionStore} from "../../../stores/clientSessionStore"
import {colours} from "../../../theme"
import {DownloadAltIcon} from "../../../assets"
import type {
  DeliverableRecentModel,
  PagedResult,
  ProjectManager,
  Token,
} from "../../../types"
import {NewButton} from "../../atoms"
import ProjectQuestionModal from "../ProjectQuestionModal/ProjectQuestionModal"
import {Trans, useTranslation} from "react-i18next"
import {useSessionStorage} from "usehooks-ts"
import {appInsights} from "../../../config/appInsights"
import {CrmAccount} from "../../../types/CrmAccount"

const DeliverablesTable: FC = () => {
  const {getRecentDeliverables, downloadDeliverableFile} =
    deliverableEndpoints()
  const {getProjectManager} = projectsEndpoints()
  const {sendFabricEvents} = fabricEndpoints()

  const {t} = useTranslation()
  const [language] = useSessionStorage("language", "en")
  const theme = useTheme()
  const navigate = useNavigate()
  const sessionClient = useClientSessionStore((state) => state.crmAccount)
  const {token} = useConfigProvider()
  const storage = useStorage("session", StorageData.crmAccount)
  const decodedToken = decodeToken<Token>(token)
  const accountNumber =
    decodedToken?.companyId === CrmAccount.Internal
      ? storage.get() || ""
      : decodedToken?.companyId || ""

  const [showError, setShowError] = useState(false)
  const [showSupportModal, setShowSupportModal] = useState<boolean>(false)
  const [showProjectQuestionModal, setShowProjectQuestionModal] =
    useState<boolean>(false)
  const [selectedDeliverable, setSelectedDeliverable] =
    useState<DeliverableRecentModel | null>(null)
  const [downloadingDeliverable, setDownloadingDeliverable] =
    useState<DeliverableRecentModel | null>(null)

  const recentDeliverablesQuery = useQuery<PagedResult<DeliverableRecentModel>>(
    {
      queryKey: ["deliverables", "recent", accountNumber],
      queryFn: () => getRecentDeliverables(accountNumber),
      enabled: !!sessionClient && !!accountNumber,
    },
  )

  const projectManagers: UseQueryResult<ProjectManager, Error>[] = useQueries({
    queries:
      recentDeliverablesQuery.data?.results?.map((deliverable) => ({
        queryKey: ["getProjectByDeliverable", deliverable.deliverableId || ""],
        queryFn: () => getProjectManager(deliverable.projectId),
        enabled: !!recentDeliverablesQuery.data,
      })) || [],
  })

  const isProjectManagerLoading = projectManagers.some((p) => p.isLoading)

  const getDeliverableFileMutation = useMutation<
    File,
    Error,
    DeliverableRecentModel
  >({
    mutationKey: ["downloadFile", downloadingDeliverable?.deliverableId],
    mutationFn: async (deliverable) => {
      const deliverableName = deliverable?.deliverableName
      const fileName = deliverableName
        ? deliverableName
            .split("/")
            .pop()
            ?.match(/.{1,30}/g)
            ?.join("\n")
        : "defaultFileName"
      const extension = deliverable?.deliverableSource?.toLowerCase()
      const contentType = extension
        ? contentTypeMap[extension as keyof typeof contentTypeMap]
        : null
      const file = await downloadDeliverableFile(
        deliverable.deliverableId || "",
        fileName || "",
        contentType,
        extension || "",
      )
      return file || Promise.reject(new Error("File not found"))
    },
  })

  const sendFabricEventsMutation = useMutation({
    mutationFn: sendFabricEvents,
    mutationKey: ["sendFabricEvents"],
  })

  const handleRefreshDeliverables = () => {
    if (accountNumber && sessionClient) {
      recentDeliverablesQuery.refetch()
    }
  }

  const handleOpenSupportModal = () => {
    setShowSupportModal((open) => !open)
  }

  const handleDownload = async (deliverable: DeliverableRecentModel) => {
    if (
      deliverable.restrictDownload ||
      !deliverable.deliverableUrl ||
      !deliverable
    ) {
      return
    }
    setDownloadingDeliverable(deliverable)

    try {
      const file = await getDeliverableFileMutation.mutateAsync(deliverable)
      if (file) {
        const fileUrl = URL.createObjectURL(file)
        const link = document.createElement("a")
        link.href = fileUrl
        link.download = `${deliverable.deliverableName || "file"}.${
          deliverable.deliverableSource || "pdf"
        }`
        // deepcode ignore DOMXSS: False positive, the injected URL comes from our API, not user input
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        const decodedToken = decodeToken<Token>(token)

        appInsights.trackEvent({
          name: ["client uploads", "your uploads"].includes(
            deliverable.folder?.folderName.toLowerCase() || "",
          )
            ? "DOWNLOAD_FILE"
            : "DOWNLOAD_DELIVERABLE",
          properties: {
            deliverableId: deliverable.deliverableId,
            deliverableName: deliverable?.deliverableName,
            folderId: deliverable.folder?.folderId,
            folderName: deliverable.folder?.folderName,
            projectId: deliverable.projectId,
            projectName:
              deliverable.projectAssignmentName ||
              deliverable.projectAssignmentDisplayName ||
              "",
            downloadedBy: decodedToken?.email || "",
            downloadedById: decodedToken?.contactId || "",
          },
        })
        const fabricEventPayload = {
          type: ["client uploads", "your uploads"].includes(
            deliverable.folder?.folderName.toLowerCase() || "",
          )
            ? "download_file"
            : "download_deliverable",
          targetId: deliverable.deliverableId!,
          targetName: deliverable?.deliverableName,
          appUrl: window.location.href,
        }
        await sendFabricEventsMutation.mutateAsync([fabricEventPayload])
      }
    } catch (error) {
      console.error("Error downloading deliverable", error)
    }

    setDownloadingDeliverable(null)
  }

  useEffect(() => {
    const refreshIntervalId = setInterval(() => {
      handleRefreshDeliverables()
    }, 300000)

    return () => clearInterval(refreshIntervalId)
  }, [])

  useEffect(() => {
    if (language) {
      moment.locale(language)
    }
  }, [language])

  return (
    <Fragment>
      <Stack
        id="my-deliverables"
        component="article"
        direction="column"
        width="100%"
        height="auto"
        gap="0.25rem"
        // paddingX="1.5rem"
        paddingY="1rem"
        bgcolor={theme.palette.common.white}
        border="1px solid #cdcdcd"
        borderRadius="0.5rem"
      >
        <Stack
          component="header"
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          paddingX="1.5rem"
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            padding="0.5rem"
            width="auto"
            height="32px"
            textAlign="center"
            bgcolor={theme.palette.success.main}
            color={theme.palette.common.white}
          >
            <Typography
              component="span"
              fontSize="0.875rem"
              fontWeight="bold"
              fontStyle="normal"
              lineHeight="1.14"
              letterSpacing="normal"
              textAlign="center"
              color="inherit"
            >
              {t("My deliverables")}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            gap="0.5rem"
            sx={{cursor: "pointer"}}
          >
            <NewButton
              size="small"
              variant="text"
              color="destructive"
              text="💣"
              onClick={() => setShowError((s) => !s)}
              sx={{
                paddingX: "0",
                minWidth: "unset",
                width: "32px",
                display: "none",
              }}
            />
            {/* @ts-ignore */}
            {showError ? decodedToken.error.find((x: any) => x.id) : null}
          </Stack>
        </Stack>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  th: {
                    paddingX: "0.75rem",
                    paddingY: "0.5rem",
                    height: "40px",
                    color: "#242d35",
                    fontSize: "0.875rem",
                    lineHeight: "1.43",
                    letterSpacing: "normal",
                    fontWeight: "normal",

                    ":first-of-type": {
                      paddingLeft: "1.5rem",
                    },
                  },
                }}
              >
                <TableCell align="left">{t("Project")}</TableCell>
                <TableCell align="left">{t("Deliverable")}</TableCell>
                <TableCell align="left">{t("Project Manager")}</TableCell>
                <TableCell align="left">{t("Last updated")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!sessionClient ||
              !accountNumber ||
              isProjectManagerLoading ||
              recentDeliverablesQuery.isLoading ||
              recentDeliverablesQuery.isFetching ||
              recentDeliverablesQuery.isRefetching ||
              (!recentDeliverablesQuery.data &&
                !recentDeliverablesQuery.isError &&
                !recentDeliverablesQuery.isLoadingError &&
                !recentDeliverablesQuery.isRefetchError) ? (
                [1, 2, 3, 4, 5, 6]?.map((index) => (
                  <TableRow
                    key={index}
                    sx={{
                      td: {
                        border: 0,
                        paddingX: "0.75rem",
                        paddingY: "0.5rem",
                        "&:first-of-type": {
                          paddingLeft: "1.5rem",
                        },
                      },
                    }}
                  >
                    <TableCell align="left" width="20%">
                      <Skeleton
                        animation="wave"
                        variant="text"
                        width="100%"
                        height={20}
                      />
                    </TableCell>
                    <TableCell align="left" width="40%">
                      <Stack direction="row" gap="0.5rem" alignItems="center">
                        <Skeleton
                          animation="wave"
                          variant="text"
                          width="100%"
                          height={20}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="left" width="20%">
                      <Stack direction="row" gap="0.5rem" alignItems="center">
                        <Skeleton
                          animation="wave"
                          variant="circular"
                          width="32px"
                          height="32px"
                        />
                        <Skeleton
                          animation="wave"
                          variant="text"
                          width="50%"
                          height={20}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="left" width="20%">
                      <Skeleton animation="wave" variant="text" width="100%" />
                    </TableCell>
                  </TableRow>
                ))
              ) : !recentDeliverablesQuery.data?.results ||
                recentDeliverablesQuery.isError ||
                recentDeliverablesQuery.isLoadingError ||
                recentDeliverablesQuery.isRefetchError ? (
                <TableRow
                  sx={{
                    td: {
                      border: 0,
                      color: theme.palette.info.dark,
                      fontSize: "0.875rem",
                      lineHeight: "1.43",
                      height: "125px",
                    },
                  }}
                >
                  <TableCell colSpan={4} align="center">
                    <Typography
                      fontSize="inherit"
                      lineHeight="inherit"
                      display="block"
                    >
                      <Trans
                        i18nKey="We're sorry, we're having trouble retrieving this right now. Please try refreshing your browser or contact us."
                        defaults="We're sorry, we're having trouble retrieving this right now. Please try refreshing your browser or <ContactLink>contact us</ContactLink>"
                        components={{
                          ContactLink: (
                            <Typography
                              component="span"
                              color="#226CA5"
                              fontSize="0.875rem"
                              sx={{
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                              onClick={handleOpenSupportModal}
                            >
                              contact us
                            </Typography>
                          ),
                        }}
                      />
                    </Typography>
                    <NewButton
                      variant="outline"
                      color="primary"
                      text={t("Refresh")}
                      onClick={handleRefreshDeliverables}
                      sx={{
                        marginTop: "1rem",
                        marginX: "auto",
                        paddingX: "18px",
                        height: "32px",
                      }}
                    />
                  </TableCell>
                </TableRow>
              ) : recentDeliverablesQuery.data.rowCount === 0 ? (
                <TableRow
                  sx={{
                    td: {
                      border: 0,
                      color: theme.palette.info.dark,
                      fontSize: "0.875rem",
                      lineHeight: "1.43",
                      height: "40px",
                    },
                  }}
                >
                  <TableCell colSpan={4} align="center">
                    <Typography
                      fontSize="inherit"
                      lineHeight="inherit"
                      display="block"
                    >
                      {t(
                        "You do not currently have any active projects. If you believe this is an error, please",
                      )}{" "}
                      &nbsp;
                      <Typography
                        component="span"
                        color="#226CA5"
                        fontSize="0.875rem"
                        sx={{textDecoration: "underline", cursor: "pointer"}}
                        onClick={handleOpenSupportModal}
                      >
                        {t("contact us")}
                      </Typography>
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recentDeliverablesQuery.data.results?.map(
                  (deliverable, index) => (
                    <TableRow
                      key={deliverable.deliverableId}
                      sx={{
                        backgroundColor: theme.palette.common.white,
                        transition: "background-color 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: colours.superSilver,
                        },
                        td: {
                          border: 0,
                          paddingX: "0.75rem",
                          paddingY: "0.5rem",
                          height: "40px",
                          color: theme.palette.info.dark,
                          fontSize: "0.875rem",
                          lineHeight: "1.43",
                          letterSpacing: "normal",
                          fontWeight: "normal",

                          "&:first-of-type": {
                            paddingLeft: "1.5rem",
                            color: "#242d35",
                          },
                        },
                      }}
                    >
                      <TableCell align="left" width="20%" sx={{width: "20%"}}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap="0.5rem"
                          color="#242d35"
                          maxWidth="100%"
                          onClick={() =>
                            navigate(`/projects/${deliverable.projectId}`)
                          }
                          sx={{overflow: "hidden", cursor: "pointer"}}
                        >
                          <TextWithTooltip
                            text={
                              deliverable.projectAssignmentDisplayName ||
                              deliverable.projectAssignmentName
                            }
                            truncationType="width"
                            textStyle={{
                              fontSize: "0.875rem",
                              lineHeight: "1.43",
                              color: "#242d35",
                            }}
                            tooltipProps={{
                              arrow: false,
                              placement: "bottom-start",
                              sx: {
                                "& .MuiTooltip-tooltip": {
                                  backgroundColor: "#FFFFFF",
                                  height: "26px",
                                  color: "#242d35",
                                  textAlign: "left",
                                  fontSize: "14px",
                                  fontWeight: "400",
                                  lineHeight: "22px",
                                  paddingX: "8px",
                                  paddingY: "2px",
                                  borderWidth: "0.5px",
                                  borderStyle: "solid",
                                  borderColor: "#1B1B1B",
                                  borderRadius: "0px",
                                },
                              },
                            }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell
                        align="left"
                        width="40%"
                        sx={{overflow: "hidden", width: "40%"}}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap="0.5rem"
                          color="#242d35"
                          flexWrap="nowrap"
                          maxWidth="100%"
                        >
                          <Stack
                            direction="row"
                            gap="0.5rem"
                            alignItems="center"
                            onClick={() => {
                              if (
                                deliverable.deliverableType === "report" &&
                                !clickableTypes.includes(
                                  deliverable.deliverableSource?.toLowerCase() ||
                                    "",
                                )
                              ) {
                                return
                              }
                              navigate(
                                `/projects/${deliverable.projectId}/folders/${deliverable.folder?.folderId}/${deliverable.deliverableType}/${deliverable.deliverableId}`,
                              )
                            }}
                            sx={{
                              cursor:
                                deliverable.deliverableType === "report" &&
                                !clickableTypes.includes(
                                  deliverable.deliverableSource?.toLowerCase() ||
                                    "",
                                )
                                  ? "initial"
                                  : "pointer",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{svg: {maxHeight: "16px"}}}
                            >
                              {deliverable.strictlyConfidential ? (
                                <Tooltip
                                  arrow
                                  title={t("Strictly confidential")}
                                  placement="right"
                                >
                                  <div>
                                    <ReactSVG
                                      src={
                                        deliverable.deliverableType ===
                                        "dashboard"
                                          ? "/icons/file-icons/Dashboard-confidential.svg"
                                          : deliverable.deliverableType ===
                                            "web"
                                          ? "/icons/file-icons/Web-report-confidential.svg"
                                          : `/icons${
                                              iconMap[
                                                getDeliverableExtension(
                                                  deliverable.deliverableUrl ||
                                                    "",
                                                )
                                              ]
                                            }-confidential.svg`
                                      }
                                    />
                                  </div>
                                </Tooltip>
                              ) : (
                                <ReactSVG
                                  src={
                                    deliverable.deliverableType === "dashboard"
                                      ? "/icons/file-icons/Dashboard.svg"
                                      : deliverable.deliverableType === "web"
                                      ? "/icons/file-icons/Web-report.svg"
                                      : `/icons${
                                          iconMap[
                                            getDeliverableExtension(
                                              deliverable.deliverableUrl || "",
                                            )
                                          ]
                                        }.svg`
                                  }
                                />
                              )}
                            </Box>
                            <Tooltip
                              arrow
                              placement="bottom-start"
                              title={t(
                                "Download this deliverable to view contents - no portal view available.",
                              )}
                              {...(deliverable.deliverableType === "report" &&
                              !clickableTypes.includes(
                                deliverable.deliverableSource?.toLowerCase() ||
                                  "",
                              )
                                ? {}
                                : {
                                    open: false,
                                    onOpen: () => {},
                                    onClose: () => {},
                                  })}
                              sx={{
                                "& .MuiTooltip-tooltip": {
                                  maxWidth: "312px",
                                  paddingX: "12px",
                                  paddingY: "5px",
                                  fontSize: "14px",
                                  fontWeight: "400",
                                  lineHeight: "22px",
                                  textAlign: "left",
                                },
                                "& .MuiTooltip-arrow": {
                                  left: "8px !important",
                                  transform: "none !important",
                                },
                              }}
                            >
                              <Stack maxWidth="100%">
                                <TextWithTooltip
                                  text={deliverable.deliverableName}
                                  truncationType="characterCount"
                                  limit={55}
                                  textStyle={{
                                    fontSize: "0.875rem",
                                    lineHeight: "1.43",
                                    color: "#242d35",
                                  }}
                                  tooltipProps={{
                                    arrow: false,
                                    placement: "bottom-start",
                                    sx: {
                                      "& .MuiTooltip-tooltip": {
                                        backgroundColor: "#FFFFFF",
                                        height: "26px",
                                        maxWidth: "100vw",
                                        color: "#242d35",
                                        textAlign: "left",
                                        fontSize: "14px",
                                        fontWeight: "400",
                                        lineHeight: "22px",
                                        paddingX: "8px",
                                        paddingY: "2px",
                                        borderWidth: "0.5px",
                                        borderStyle: "solid",
                                        borderColor: "#1B1B1B",
                                        borderRadius: "0px",
                                        marginTop:
                                          deliverable.deliverableType ===
                                            "report" &&
                                          !clickableTypes.includes(
                                            deliverable.deliverableSource?.toLowerCase() ||
                                              "",
                                          )
                                            ? "78px !important"
                                            : undefined,
                                      },
                                    },
                                  }}
                                />
                              </Stack>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell align="left" width="20%">
                        <Stack direction="row" gap="0.5rem" alignItems="center">
                          {deliverable.projectAssignmentManagers.length > 0 ? (
                            <Fragment>
                              {projectManagers[index].data
                                ?.projectManagerName === "Not applicable" ? (
                                <Typography
                                  component="span"
                                  fontSize="0.875rem"
                                  fontWeight="normal"
                                  fontStyle="normal"
                                  lineHeight="1.43"
                                  letterSpacing="normal"
                                  textAlign="left"
                                  color="inherit"
                                >
                                  {t("Not applicable")}
                                </Typography>
                              ) : !!projectManagers[index].data
                                  ?.projectManagerName ? (
                                <Stack
                                  direction="row"
                                  gap="6px"
                                  alignItems="center"
                                  sx={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    setSelectedDeliverable(deliverable)
                                    setShowProjectQuestionModal(true)
                                  }}
                                >
                                  <Avatar
                                    // deepcode ignore DOMXSS: False positive, the injected URL comes from our API, not user input
                                    {...stringToAvatar(
                                      `${projectManagers[index].data?.projectManagerName}`,
                                      {
                                        width: "1.5rem",
                                        height: "1.5rem",
                                        fontSize: "0.75rem",
                                        lineHeight: "1.33",
                                        letterSpacing: "normal",
                                        fontWeight: "normal",
                                      },
                                    )}
                                  >
                                    {}
                                  </Avatar>
                                  <TextWithTooltip
                                    limit={18}
                                    text={
                                      projectManagers[index].data
                                        ?.projectManagerName
                                    }
                                    includeEllipsis={false}
                                    tooltipStyle={{
                                      "& .MuiTooltip-tooltip": {
                                        mt: "3px !important",
                                        textAlign: "left",
                                        borderRadius: 0,
                                        border: "1px solid #1B1B1B",
                                        bgcolor: "#FFF",
                                        color: "#242D35",
                                        p: "2px 8px",
                                      },
                                    }}
                                    textStyle={{
                                      fontSize: "0.875rem",
                                      fontWeight: "normal",
                                      fontStyle: "normal",
                                      lineHeight: "1.43",
                                      letterSpacing: "normal",
                                      textAlign: "left",
                                      color: "inherit",
                                    }}
                                  />
                                </Stack>
                              ) : null}
                            </Fragment>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell
                        align="left"
                        width="20%"
                        sx={{
                          paddingY: "0 !important",
                          paddingRight: "0 !important",
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap="12px"
                          flexWrap="nowrap"
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            height="40px"
                            minWidth="94px"
                          >
                            <Typography
                              fontSize="0.875rem"
                              lineHeight="1.43"
                              letterSpacing="normal"
                              textAlign="left"
                              color={theme.palette.info.dark}
                              fontWeight="400"
                            >
                              {!language || language === "en"
                                ? moment(
                                    deliverable.lastUpdateTimestamp ||
                                      deliverable.createdTimestamp,
                                  )
                                    .format("D MMM, YYYY")
                                    .replace(".", "")
                                : moment(
                                    deliverable.lastUpdateTimestamp ||
                                      deliverable.createdTimestamp,
                                  )
                                    .format("D MMM, YYYY")
                                    .replace(".", "")}
                            </Typography>
                          </Stack>
                          <Tooltip
                            arrow
                            placement="bottom-start"
                            title={
                              deliverable.restrictDownload ||
                              !deliverable.deliverableUrl ||
                              deliverable.deliverableType === "dashboard"
                                ? t("Unavailable")
                                : downloadingDeliverable?.deliverableId ===
                                  deliverable.deliverableId
                                ? t("Downloading")
                                : t("Download")
                            }
                            sx={{
                              "& .MuiTooltip-arrow": {
                                left: "16px !important",
                                transform: "none !important",
                              },
                            }}
                          >
                            <Box>
                              <NewButton
                                icon
                                size="medium"
                                variant="text"
                                IconLeft={
                                  downloadingDeliverable?.deliverableId ===
                                  deliverable.deliverableId ? (
                                    <CircularProgress
                                      size="14px"
                                      sx={{
                                        color: "#053747",
                                      }}
                                    />
                                  ) : (
                                    <DownloadAltIcon
                                      width="14px"
                                      height="14px"
                                    />
                                  )
                                }
                                onClick={
                                  deliverable.restrictDownload ||
                                  !deliverable.deliverableUrl ||
                                  deliverable.deliverableType === "dashboard" ||
                                  downloadingDeliverable?.deliverableId ===
                                    deliverable.deliverableId
                                    ? (e) => {
                                        e.stopPropagation()
                                        e.preventDefault()
                                      }
                                    : (e) => {
                                        e.stopPropagation()
                                        handleDownload(deliverable)
                                      }
                                }
                                disabled={
                                  deliverable.restrictDownload ||
                                  !deliverable.deliverableUrl ||
                                  deliverable.deliverableType === "dashboard"
                                }
                                sx={{
                                  minWidth: "unset",
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "4px",
                                  padding: "0",
                                  "&:hover": {
                                    backgroundColor:
                                      deliverable.restrictDownload ||
                                      !deliverable.deliverableUrl ||
                                      deliverable.deliverableType ===
                                        "dashboard"
                                        ? "transparent"
                                        : "#dedede",
                                  },
                                  "&:active": {
                                    backgroundColor:
                                      deliverable.restrictDownload ||
                                      !deliverable.deliverableUrl ||
                                      deliverable.deliverableType ===
                                        "dashboard"
                                        ? "transparent"
                                        : "#cdcdcd",
                                  },
                                  svg: {
                                    color: "inherit",
                                    width: "14px",
                                    height: "14px",
                                    "g, path": {
                                      fill:
                                        deliverable.restrictDownload ||
                                        !deliverable.deliverableUrl ||
                                        deliverable.deliverableType ===
                                          "dashboard"
                                          ? "#8b8b8a"
                                          : "#053747",
                                    },
                                  },
                                }}
                              />
                            </Box>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ),
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
      <SupportModal
        show={showSupportModal}
        handleToggle={handleOpenSupportModal}
        title="New client support request"
      />
      <ProjectQuestionModal
        open={showProjectQuestionModal}
        onClose={() => setShowProjectQuestionModal(false)}
        selectedDeliverable={selectedDeliverable}
      />
    </Fragment>
  )
}

DeliverablesTable.displayName = "DeliverablesTable"

export default DeliverablesTable
